/**
 * A review comment's anchor — what the reviewer's feedback is attached to.
 *
 * The review cycle is shared between two project kinds:
 *   - video reviews anchor to a slide + timecode in the player
 *   - assessment reviews anchor to a question (no timeline)
 *
 * The queue / chip stack / prompt builder are anchor-agnostic; only the panels
 * that capture an anchor (video ReviewsPanel, AssessmentReviewsPanel) know which
 * kind they produce.
 */

export interface SlideAnchor {
  kind: 'slide';
  slideId: string;
  slideIndex: number;
  slideTitle: string;
  /** Seconds into the full video at the moment of the comment. */
  timestamp: number;
}

export interface QuestionAnchor {
  kind: 'question';
  questionId: string;
  questionIndex: number;
  questionStem: string;
  format: string;
}

export type ReviewAnchor = SlideAnchor | QuestionAnchor;

/** mm:ss timecode (or h:mm:ss for >= 1h). */
function timecode(seconds: number): string {
  const total = Math.max(0, Math.floor(seconds));
  const h = Math.floor(total / 3600);
  const m = Math.floor((total % 3600) / 60);
  const s = total % 60;
  if (h > 0) return `${h}:${String(m).padStart(2, '0')}:${String(s).padStart(2, '0')}`;
  return `${m}:${String(s).padStart(2, '0')}`;
}

/** True when a slide title is a real label rather than a fallback to the slide id. */
function isRealSlideTitle(title: string, id: string): boolean {
  const t = (title || '').trim();
  return !!t && t !== id && !/^slide-\d+$/i.test(t);
}

/** Compact chip label shown in the composer chip — "S2 · 0:14" or "Q3 · numeric". */
export function anchorChipLabel(a: ReviewAnchor): string {
  if (a.kind === 'slide') return `S${a.slideIndex + 1} · ${timecode(a.timestamp)}`;
  return `Q${a.questionIndex + 1}${a.format ? ` · ${a.format}` : ''}`;
}

/** The bold heading line for one anchor group in the chat prompt. */
export function anchorPromptHeading(a: ReviewAnchor): string {
  if (a.kind === 'slide') {
    const suffix = isRealSlideTitle(a.slideTitle, a.slideId) ? ` — "${a.slideTitle}"` : '';
    return `**Slide ${a.slideIndex + 1}${suffix} (\`${a.slideId}\`)**`;
  }
  const stem = a.questionStem?.trim();
  const suffix = stem ? ` — "${stem.slice(0, 80)}"` : '';
  return `**Question ${a.questionIndex + 1}${suffix} (\`${a.questionId}\`)**`;
}

/** The per-comment bullet prefix (slides carry a timecode; questions don't). */
export function anchorBulletPrefix(a: ReviewAnchor): string {
  return a.kind === 'slide' ? `At ${timecode(a.timestamp)}, ` : '';
}

/** Stable grouping key so multiple comments on the same target collapse together. */
export function anchorGroupKey(a: ReviewAnchor): string {
  return a.kind === 'slide' ? `slide:${a.slideId}` : `question:${a.questionId}`;
}
